import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/context/AuthContext';
import {
  Crown, Shield, Truck, Package, CheckCircle2, AlertTriangle, Loader2, MapPin, Lock,
  Landmark, PlusCircle, ChevronRight, Signature, ScanFace,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_META = {
  pickup:    { Icon: Truck,       cls: 'text-blue-600',    label: 'Pickup',        next: 'Armored courier collected the parcel.' },
  seal:      { Icon: Lock,        cls: 'text-slate-700',   label: 'Vault seal',    next: 'Parcel sealed with tamper-evident case.' },
  midway:    { Icon: MapPin,      cls: 'text-amber-600',   label: 'Midway hub',    next: 'Handoff between origin & destination agent.' },
  delivery:  { Icon: Truck,       cls: 'text-indigo-600',  label: 'Out for delivery', next: 'Destination agent en route to recipient.' },
  signoff:   { Icon: Signature,   cls: 'text-emerald-600', label: 'Recipient sign-off', next: 'Recipient verified and possession recorded.' },
  exception: { Icon: AlertTriangle, cls: 'text-rose-600',  label: 'Exception',     next: 'Unusual event — see notes.' },
};

const STATUS_LABEL = {
  enrolled: 'Enrolled',
  picked_up: 'Picked up',
  vaulted: 'In vault',
  in_transit: 'In transit',
  out_for_delivery: 'Out for delivery',
  delivered: 'Delivered',
  exception: 'Exception',
};

export default function EliteShipmentsPage() {
  const { token } = useAuth();
  const auth = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
  const [list, setList] = useState([]);
  const [selected, setSelected] = useState(null);
  const [enrolling, setEnrolling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({
    declared_value: 1000000,
    item_description: 'Investment-grade collectible',
    from_addr: { name: 'SelleSend Elite Vault SD', street1: '4395 Home Ave', city: 'San Diego', state: 'CA', zip: '92105' },
    to_addr: { name: 'Bob Recipient', street1: '350 5th Ave', city: 'New York', state: 'NY', zip: '10118' },
  });

  const load = async () => {
    const r = await fetch(`${API}/elite`, { headers: auth });
    if (r.ok) setList((await r.json()).shipments || []);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const enroll = async () => {
    setBusy(true); setErr(null);
    const r = await fetch(`${API}/elite/enroll`, {
      method: 'POST', headers: auth,
      body: JSON.stringify({
        declared_value: Number(form.declared_value),
        item_description: form.item_description,
        from_address: form.from_addr,
        to_address: form.to_addr,
        insurance_tier: 'D',
        two_agent_required: true,
      }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) setErr(j.detail || `Enroll failed (HTTP ${r.status})`);
    else { setEnrolling(false); await load(); setSelected(j); }
    setBusy(false);
  };

  const appendCustody = async (shipId, step) => {
    setBusy(true);
    const r = await fetch(`${API}/elite/${shipId}/custody`, {
      method: 'POST', headers: auth,
      body: JSON.stringify({
        step, agent_id: `AGT-${Math.random().toString(36).slice(2,8).toUpperCase()}`,
        agent_name: `Agent · ${step}`,
        signature_hash: '0'.repeat(64),
        seal_id: step === 'seal' ? `SEAL-${Date.now()}` : undefined,
        geo: { lat: 32.71, lng: -117.16 },
        notes: `${step} recorded at ${new Date().toLocaleTimeString()}`,
      }),
    });
    if (r.ok) {
      const detail = await fetch(`${API}/elite/${shipId}`, { headers: auth }).then((x) => x.json());
      setSelected(detail);
      await load();
    }
    setBusy(false);
  };

  const complete = async (shipId) => {
    setBusy(true);
    const r = await fetch(`${API}/elite/${shipId}/complete`, {
      method: 'POST', headers: auth,
      body: JSON.stringify({
        recipient_name: 'Bob Recipient',
        recipient_id_type: 'driver_license',
        recipient_id_last4: '4242',
        signature_hash: '0'.repeat(64),
      }),
    });
    const j = await r.json();
    if (!r.ok) setErr(j.detail || 'Complete failed');
    else {
      const detail = await fetch(`${API}/elite/${shipId}`, { headers: auth }).then((x) => x.json());
      setSelected(detail); await load();
    }
    setBusy(false);
  };

  return (
    <div className="space-y-6" data-testid="elite-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold flex items-center gap-2">
            <Crown className="w-7 h-7 text-amber-500" />
            SelleGuard Elite
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium ml-2">$1M+ concierge</span>
          </h1>
          <p className="text-sm text-slate-500 mt-1">Vault-to-vault armored delivery with 2-agent chain of custody &amp; published Merkle-root anchoring.</p>
        </div>
        <Button onClick={() => setEnrolling(true)} data-testid="elite-enroll-open">
          <PlusCircle className="w-4 h-4 mr-2" />New Elite shipment
        </Button>
      </div>

      {err && (
        <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-lg flex items-center gap-2" data-testid="elite-error">
          <AlertTriangle className="w-4 h-4" />{err}
        </div>
      )}

      {enrolling && (
        <Card className="border-amber-200 dark:border-amber-800" data-testid="elite-enroll-form">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Landmark className="w-4 h-4" />Enroll new Elite shipment</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500">Declared value (USD)</label>
                <Input type="number" min="1000000" step="10000" value={form.declared_value}
                       onChange={(e) => setForm(f => ({ ...f, declared_value: e.target.value }))}
                       data-testid="elite-declared-value" />
              </div>
              <div>
                <label className="text-xs text-slate-500">Item description</label>
                <Input value={form.item_description}
                       onChange={(e) => setForm(f => ({ ...f, item_description: e.target.value }))}
                       data-testid="elite-item-description" />
              </div>
            </div>
            <p className="text-xs text-slate-400 pt-1">Origin: {form.from_addr.street1}, {form.from_addr.city} · Destination: {form.to_addr.city}, {form.to_addr.state}</p>
            <div className="flex gap-2 pt-2">
              <Button onClick={enroll} disabled={busy} data-testid="elite-enroll-submit">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Shield className="w-4 h-4 mr-2" />}Enroll &amp; open concierge desk
              </Button>
              <Button variant="ghost" onClick={() => setEnrolling(false)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 space-y-2" data-testid="elite-list">
          {list.length === 0 && !enrolling && (
            <div className="p-6 rounded-lg border-2 border-dashed text-center text-sm text-slate-400" data-testid="elite-empty">
              No Elite shipments yet. Click "New Elite shipment" to open your first concierge case.
            </div>
          )}
          {list.map(s => (
            <button key={s.id} onClick={() => setSelected(s)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${selected?.id === s.id ? 'border-amber-400 bg-amber-50/40' : 'border-slate-200 hover:border-amber-300'}`}
                    data-testid={`elite-list-item-${s.id.slice(0,8)}`}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono text-slate-500">{s.id.slice(0, 8)}</span>
                <span className="text-[10px] font-semibold uppercase text-amber-600">{STATUS_LABEL[s.status] || s.status}</span>
              </div>
              <p className="text-sm font-medium mt-0.5 truncate">{s.item_description}</p>
              <p className="text-xs text-slate-500 mt-0.5">${Number(s.declared_value).toLocaleString()} → {s.to_address?.city}, {s.to_address?.state}</p>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2">
          {selected ? (
            <Card data-testid="elite-detail">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  Elite shipment · <span className="font-mono text-xs">{selected.id.slice(0, 8)}</span>
                  <span className="ml-auto text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full">{STATUS_LABEL[selected.status] || selected.status}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><span className="text-slate-400">Declared:</span> <b>${Number(selected.declared_value).toLocaleString()}</b></div>
                  <div><span className="text-slate-400">Tier:</span> <b>Insurance {selected.insurance_tier} · Elite</b></div>
                  <div className="col-span-2"><span className="text-slate-400">Item:</span> {selected.item_description}</div>
                  <div><span className="text-slate-400">From:</span> {selected.from_address?.street1}, {selected.from_address?.city}</div>
                  <div><span className="text-slate-400">To:</span> {selected.to_address?.street1}, {selected.to_address?.city}</div>
                  <div className="col-span-2 pt-1 border-t border-slate-100 dark:border-slate-800">
                    <span className="text-slate-400">Anchor:</span>
                    <span className="font-mono text-[10px] break-all ml-1">{selected.blockchain?.data_hash}</span>
                  </div>
                </div>

                {/* Custody actions */}
                <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                  <span className="text-xs text-slate-500 mr-2 self-center">Log custody event:</span>
                  {['pickup','seal','midway','delivery'].map(step => (
                    <Button key={step} size="sm" variant="outline" onClick={() => appendCustody(selected.id, step)} disabled={busy || selected.status === 'delivered'} data-testid={`elite-custody-${step}`}>
                      <ChevronRight className="w-3 h-3 mr-1" />{STEP_META[step].label}
                    </Button>
                  ))}
                  <Button size="sm" onClick={() => complete(selected.id)} disabled={busy || selected.status === 'delivered'} data-testid="elite-complete" className="bg-emerald-600 hover:bg-emerald-700">
                    <ScanFace className="w-3 h-3 mr-1" />Recipient sign-off
                  </Button>
                </div>

                {/* Timeline */}
                <div className="space-y-2 pt-2">
                  {(selected.chain_of_custody || []).length === 0 && (
                    <div className="text-xs text-slate-400 italic">No custody events yet. Start by logging the pickup.</div>
                  )}
                  {(selected.chain_of_custody || []).map((e, i) => {
                    const meta = STEP_META[e.step] || STEP_META.exception;
                    const IconEl = meta.Icon;
                    return (
                      <div key={e.id || i} className="flex gap-3 items-start p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50" data-testid={`elite-event-${e.step}`}>
                        <IconEl className={`w-4 h-4 mt-0.5 ${meta.cls}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <b className="text-xs uppercase">{meta.label}</b>
                            <span className="text-[10px] text-slate-400">{new Date(e.at).toLocaleString()}</span>
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">{e.notes || meta.next}</p>
                          <p className="text-[10px] text-slate-400 mt-0.5 font-mono truncate">Anchor: {e.blockchain?.data_hash}</p>
                          {e.seal_id && <p className="text-[10px] text-indigo-500 mt-0.5">Seal ID: <span className="font-mono">{e.seal_id}</span></p>}
                          {e.recipient_name && <p className="text-[10px] text-emerald-600 mt-0.5">Recipient: {e.recipient_name} · {e.recipient_id_type} ****{e.recipient_id_last4}</p>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {selected.status === 'delivered' && (
                  <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 flex items-center gap-2 text-sm" data-testid="elite-delivered-banner">
                    <CheckCircle2 className="w-5 h-5" />
                    Delivered with full 2-agent chain of custody. All events fingerprinted &amp; anchored on the SelleGuard-Elite chain.
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="p-12 rounded-lg border-2 border-dashed text-center text-sm text-slate-400">
              <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              Select a shipment to see its concierge dashboard.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
